'use es6';

import * as CategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/CategorySlug';
import * as SuperCategorySlug from 'ecosystem-marketplace-core/constants/urlSlugs/SuperCategorySlug';
import { useCollections } from 'marketplace-ui-common/collections/hooks/useCollections';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch } from 'react-router-dom';
import Storefront from '../containers/Storefront';
import AllReviewsPage from '../containers/detail/AllReviewsPage';
import DetailContainer from '../containers/detail/DetailContainer';
import SearchResults from '../containers/storefront/SearchResults';
import StorefrontCollectionContainer from '../containers/storefront/StorefrontCollectionContainer';
import { useAppContext } from '../context/AppContext.tsx';
import withCategoryParamValidation from '../decorators/withCategoryParamValidation';
import { PublicOnly, getIsUngatedForGenericDetailsUrls } from '../selectors/auth';
import AllAppCollectionsContainer from '../storefront/containers/AllAppCollectionsContainer';
import DetailPage from './detail/DetailPage';
import NotFoundError from './error/NotFoundError';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const categorySlugRegExp = Object.values(CategorySlug).join('|');
const superCategorySlugRegExp = Object.values(SuperCategorySlug).join('|');
const pageRegExp = '[1-9][0-9]*';
const getPaginatedRoutes = (route, pageRoute) => [`${pageRoute != null ? pageRoute : route}/page/:page(${pageRegExp})`, route];
export const allCategoriesRoutes = getPaginatedRoutes('/', '');
const categoryRouteBase = `/:superCategory(${superCategorySlugRegExp})/:category(${categorySlugRegExp})`;
export const categoryRoutes = getPaginatedRoutes(`${categoryRouteBase}?`);
export const detailPageRoutes = [`${categoryRouteBase}/:slug/write-review`, `${categoryRouteBase}/:slug`, '/_detail/:slug/write-review', '/_detail/:slug', '/_preview/:slug'];
const ungatedDetailPageRoutes = [`${categoryRouteBase}/:slug/write-review`, `${categoryRouteBase}/:slug`, '/_detail/:slug/write-review', '/_detail/:slug', '/_preview/:slug', '/:slug/write-review', '/:slug'];
const allReviewsPageRoutes = [`${categoryRouteBase}/:slug/reviews`, '/_detail/:slug/reviews'];
const ungatedAllReviewsPageRoutes = [`${categoryRouteBase}/:slug/reviews`, '/_detail/:slug/reviews', '/:slug/reviews'];
export const CategorySearchResults = withCategoryParamValidation(SearchResults);
export const DetailRouteContainer = withCategoryParamValidation(props => {
  const {
    match: {
      params: {
        slug
      }
    }
  } = props;
  return /*#__PURE__*/_jsx(DetailContainer, Object.assign({}, props), slug);
});
const NotFoundRoute = () => /*#__PURE__*/_jsxs(NavMarker, {
  name: "NOT_FOUND_ERROR_LOAD",
  children: [/*#__PURE__*/_jsx(NotFoundError, {}), /*#__PURE__*/_jsx(PublicOnly, {
    children: /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx(PrerenderReady, {
        statusCode: 404
      })
    })
  })]
});
export default function Routes() {
  const appContext = useAppContext();
  const isUngatedForGenericDetailsUrls = useSelector(getIsUngatedForGenericDetailsUrls);
  const {
    getCollectionSlugs,
    requestState: collectionsRequestState
  } = useCollections({
    appContext,
    offeringType: 'APP'
  });
  const collectionSlugs = getCollectionSlugs();
  const collectionRoutes = useMemo(() => getPaginatedRoutes(`/:collection(${collectionSlugs.join('|')})`), [collectionSlugs]);
  if (!isFulfilled(collectionsRequestState)) {
    return null;
  }
  const renderedDetailPageRoutes = isUngatedForGenericDetailsUrls ? /*#__PURE__*/_jsx(Route, {
    exact: true,
    sensitive: true,
    path: [...ungatedDetailPageRoutes, ...ungatedAllReviewsPageRoutes],
    render: props => /*#__PURE__*/_jsx(DetailRouteContainer, Object.assign({}, props, {
      children: /*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: `${categoryRouteBase}/:slug/write-review`,
          to: "/:slug/write-review"
        }), /*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: "/_detail/:slug/write-review",
          to: "/:slug/write-review"
        }), /*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: `${categoryRouteBase}/:slug/reviews`,
          to: "/:slug/reviews"
        }), /*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: "/_detail/:slug/reviews",
          to: "/:slug/reviews"
        }), /*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: `${categoryRouteBase}/:slug`,
          to: "/:slug"
        }), /*#__PURE__*/_jsx(Redirect, {
          sensitive: true,
          from: "/_detail/:slug",
          to: "/:slug"
        }), /*#__PURE__*/_jsx(Route, {
          path: ungatedAllReviewsPageRoutes,
          component: AllReviewsPage
        }), /*#__PURE__*/_jsx(Route, {
          path: ungatedDetailPageRoutes,
          component: DetailPage
        })]
      })
    }))
  }) : /*#__PURE__*/_jsx(Route, {
    exact: true,
    sensitive: true,
    path: [...detailPageRoutes, ...allReviewsPageRoutes],
    render: props => /*#__PURE__*/_jsx(DetailRouteContainer, Object.assign({}, props, {
      children: /*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          path: allReviewsPageRoutes,
          component: AllReviewsPage
        }), /*#__PURE__*/_jsx(Route, {
          path: detailPageRoutes,
          component: DetailPage
        })]
      })
    }))
  });
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(Route, {
      exact: true,
      sensitive: true,
      path: ['/featured', ...allCategoriesRoutes, ...collectionRoutes, ...categoryRoutes],
      render: props => /*#__PURE__*/_jsx(Storefront, Object.assign({}, props, {
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: "/featured",
            component: AllAppCollectionsContainer
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: allCategoriesRoutes,
            component: SearchResults
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: collectionRoutes,
            component: StorefrontCollectionContainer
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            sensitive: true,
            path: categoryRoutes,
            component: CategorySearchResults
          })]
        })
      }))
    }), isUngatedForGenericDetailsUrls && /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: "/:slug/review",
      to: "/:slug/write-review"
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: `${categoryRouteBase}/:slug/review`,
      to: `${categoryRouteBase}/:slug/write-review`
    }), /*#__PURE__*/_jsx(Redirect, {
      exact: true,
      sensitive: true,
      from: "/_detail/:slug/review",
      to: "/_detail/:slug/write-review"
    }), renderedDetailPageRoutes, /*#__PURE__*/_jsx(Route, {
      component: NotFoundRoute
    })]
  });
}